/**
 * Environment Validation and Health Check System
 * Provides comprehensive validation for environment configuration and system health
 */

import { EnvironmentConfig } from "../config/environment.js";
import { StripeConfig } from "../config/stripe.js";
import { FeatureFlags } from "../config/features.js";
import { Logger } from "./logger.js";

export class ValidationService {
  static validationResults = new Map();

  // Initialize validation service
  static initialize() {
    Logger.info("🔍 Validation service initialized");
    return this.runFullValidation();
  }

  // Run comprehensive validation
  static runFullValidation() {
    const results = {
      timestamp: new Date().toISOString(),
      environment: EnvironmentConfig.environment,
      overall: { passed: 0, failed: 0, warnings: 0 },
      categories: {},
    };

    // Run all validation categories
    results.categories.environment = this.validateEnvironment();
    results.categories.firebase = this.validateFirebase();
    results.categories.stripe = this.validateStripe();
    results.categories.features = this.validateFeatureFlags();
    results.categories.security = this.validateSecurity();
    results.categories.performance = this.validatePerformance();
    results.categories.dependencies = this.validateDependencies();
    results.categories.browser = this.validateBrowserSupport();

    // Calculate overall results
    Object.values(results.categories).forEach((category) => {
      results.overall.passed += category.passed || 0;
      results.overall.failed += category.failed || 0;
      results.overall.warnings += category.warnings || 0;
    });

    // Store results
    this.validationResults.set("full", results);

    // Log summary
    this.logValidationSummary(results);

    return results;
  }

  // Environment configuration validation
  static validateEnvironment() {
    const checks = [];
    let passed = 0,
      failed = 0,
      warnings = 0;

    // Check environment value
    checks.push(
      this.createCheck(
        "Environment Variable",
        () => {
          const validEnvs = ["local", "development", "staging", "production"];
          return validEnvs.includes(EnvironmentConfig.environment);
        },
        `Environment is set to: ${EnvironmentConfig.environment}`,
        `Invalid environment: ${EnvironmentConfig.environment}`
      )
    );

    // Check required environment variables
    const requiredVars = [
      "VUE_APP_FIREBASE_API_KEY",
      "VUE_APP_FIREBASE_AUTH_DOMAIN",
      "VUE_APP_FIREBASE_PROJECT_ID",
    ];

    requiredVars.forEach((varName) => {
      checks.push(
        this.createCheck(
          `Required Variable: ${varName}`,
          () => !!process.env[varName],
          `${varName} is configured`,
          `Missing required environment variable: ${varName}`
        )
      );
    });

    // Check API URL configuration
    checks.push(
      this.createCheck(
        "API URL Configuration",
        () => !!EnvironmentConfig.api.baseUrl,
        `API URL configured: ${EnvironmentConfig.api.baseUrl}`,
        "API URL is not configured"
      )
    );

    // Check build information
    const buildInfo = EnvironmentConfig.buildInfo;
    checks.push(
      this.createCheck(
        "Build Information",
        () => buildInfo.version && buildInfo.gitCommit !== "unknown",
        "Build information is available",
        "Build information is incomplete"
      )
    );

    // Process checks
    checks.forEach((check) => {
      if (check.result === "pass") passed++;
      else if (check.result === "fail") failed++;
      else warnings++;
    });

    return { category: "environment", checks, passed, failed, warnings };
  }

  // Firebase configuration validation
  static validateFirebase() {
    const checks = [];
    let passed = 0,
      failed = 0,
      warnings = 0;

    const firebaseConfig = EnvironmentConfig.firebase;
    const requiredKeys = [
      "apiKey",
      "authDomain",
      "projectId",
      "storageBucket",
      "messagingSenderId",
      "appId",
    ];

    // Check required Firebase configuration
    requiredKeys.forEach((key) => {
      checks.push(
        this.createCheck(
          `Firebase ${key}`,
          () => !!firebaseConfig[key],
          `Firebase ${key} is configured`,
          `Missing Firebase ${key}`
        )
      );
    });

    // Check Firebase project ID format
    checks.push(
      this.createCheck(
        "Firebase Project ID Format",
        () => {
          const projectId = firebaseConfig.projectId;
          return projectId && /^[a-z0-9-]+$/.test(projectId);
        },
        "Firebase project ID format is valid",
        "Firebase project ID format is invalid"
      )
    );

    // Check Firebase connection (if available)
    if (typeof window !== "undefined" && window.firebase) {
      checks.push(
        this.createCheck(
          "Firebase SDK Loaded",
          () => true,
          "Firebase SDK is loaded",
          "Firebase SDK not found"
        )
      );
    }

    // Process checks
    checks.forEach((check) => {
      if (check.result === "pass") passed++;
      else if (check.result === "fail") failed++;
      else warnings++;
    });

    return { category: "firebase", checks, passed, failed, warnings };
  }

  // Stripe configuration validation
  static validateStripe() {
    const checks = [];
    let passed = 0,
      failed = 0,
      warnings = 0;

    const stripeValidation = StripeConfig.validateConfiguration();

    // Overall Stripe configuration
    checks.push(
      this.createCheck(
        "Stripe Configuration",
        () => stripeValidation.isValid,
        "Stripe configuration is valid",
        `Stripe configuration issues: ${stripeValidation.errors.join(", ")}`
      )
    );

    // Stripe key environment compatibility
    checks.push(
      this.createCheck(
        "Stripe Key Environment Match",
        () => StripeConfig.isEnvironmentCompatible(),
        "Stripe keys match environment",
        "Stripe keys do not match current environment"
      )
    );

    // Feature flag compatibility
    if (FeatureFlags.payments.mockPayments) {
      checks.push(
        this.createCheck(
          "Mock Payments Configuration",
          () => !EnvironmentConfig.isProduction,
          "Mock payments are appropriately configured",
          "Mock payments enabled in production environment"
        )
      );
    }

    // Webhook configuration
    if (StripeConfig.webhookConfig.endpoint) {
      checks.push(
        this.createCheck(
          "Webhook Configuration",
          () => !!StripeConfig.webhookConfig.secret,
          "Webhook is properly configured",
          "Webhook endpoint configured but missing secret"
        )
      );
    }

    // Process checks
    checks.forEach((check) => {
      if (check.result === "pass") passed++;
      else if (check.result === "fail") failed++;
      else warnings++;
    });

    return { category: "stripe", checks, passed, failed, warnings };
  }

  // Feature flags validation
  static validateFeatureFlags() {
    const checks = [];
    let passed = 0,
      failed = 0,
      warnings = 0;

    const featureValidation = FeatureFlags.validateFeatures();

    // Overall feature flag validation
    checks.push(
      this.createCheck(
        "Feature Flags Validation",
        () => featureValidation.errors.length === 0,
        "All feature flags are properly configured",
        `Feature flag errors: ${featureValidation.errors.join(", ")}`
      )
    );

    // Production-specific checks
    if (EnvironmentConfig.isProduction) {
      checks.push(
        this.createCheck(
          "Production Feature Safety",
          () =>
            !FeatureFlags.development.debugPanel &&
            !FeatureFlags.development.mockData,
          "Development features disabled in production",
          "Development features are enabled in production"
        )
      );
    }

    // Development-specific checks
    if (EnvironmentConfig.isDevelopment) {
      checks.push(
        this.createCheck(
          "Development Tools Available",
          () =>
            FeatureFlags.development.debugPanel ||
            FeatureFlags.development.testUtilities,
          "Development tools are available",
          "Development tools are disabled"
        )
      );
    }

    // Integration checks
    if (FeatureFlags.integrations.googleAnalytics) {
      checks.push(
        this.createCheck(
          "Google Analytics Configuration",
          () => !!EnvironmentConfig.monitoring.googleAnalyticsId,
          "Google Analytics is properly configured",
          "Google Analytics feature enabled but no ID configured"
        )
      );
    }

    // Process checks
    checks.forEach((check) => {
      if (check.result === "pass") passed++;
      else if (check.result === "fail") failed++;
      else warnings++;
    });

    return { category: "features", checks, passed, failed, warnings };
  }

  // Security validation
  static validateSecurity() {
    const checks = [];
    let passed = 0,
      failed = 0,
      warnings = 0;

    // HTTPS check
    if (typeof window !== "undefined") {
      checks.push(
        this.createCheck(
          "HTTPS Protocol",
          () =>
            EnvironmentConfig.isLocal || window.location.protocol === "https:",
          "Using secure HTTPS protocol",
          "Not using HTTPS in production environment"
        )
      );
    }

    // Content Security Policy
    checks.push(
      this.createCheck(
        "Content Security Policy",
        () =>
          !EnvironmentConfig.isProduction ||
          EnvironmentConfig.security.enableCSP,
        "CSP is configured for production",
        "Content Security Policy not enabled in production"
      )
    );

    // Environment variable exposure
    checks.push(
      this.createCheck(
        "Environment Variable Security",
        () => {
          // Check for accidentally exposed secrets
          const exposedSecrets = Object.keys(process.env)
            .filter(
              (key) =>
                key.includes("SECRET") ||
                key.includes("PRIVATE") ||
                key.includes("KEY")
            )
            .filter((key) => !key.startsWith("VUE_APP_"));

          return exposedSecrets.length === 0;
        },
        "No sensitive environment variables exposed to client",
        "Potentially sensitive environment variables detected"
      )
    );

    // Authentication requirement
    checks.push(
      this.createCheck(
        "Authentication Requirement",
        () => EnvironmentConfig.security.requireAuth,
        "Authentication is required",
        "Authentication is not required - potential security risk"
      )
    );

    // Process checks
    checks.forEach((check) => {
      if (check.result === "pass") passed++;
      else if (check.result === "fail") failed++;
      else warnings++;
    });

    return { category: "security", checks, passed, failed, warnings };
  }

  // Performance validation
  static validatePerformance() {
    const checks = [];
    let passed = 0,
      failed = 0,
      warnings = 0;

    // Performance monitoring (intentionally disabled)
    checks.push(
      this.createCheck(
        "Performance Monitoring",
        () => true, // Always pass since it's intentionally disabled
        "Performance monitoring is intentionally disabled",
        "Performance monitoring configuration error"
      )
    );

    // Caching configuration
    checks.push(
      this.createCheck(
        "Caching Configuration",
        () =>
          EnvironmentConfig.cache.enabled || EnvironmentConfig.isDevelopment,
        "Caching is properly configured",
        "Caching not enabled for performance"
      )
    );

    // Service Worker (for production)
    if (EnvironmentConfig.isProduction) {
      checks.push(
        this.createCheck(
          "Service Worker",
          () =>
            typeof navigator !== "undefined" && "serviceWorker" in navigator,
          "Service Worker support available",
          "Service Worker not supported"
        )
      );
    }

    // Memory usage (if available)
    if (typeof window !== "undefined" && "memory" in performance) {
      checks.push(
        this.createCheck(
          "Memory Usage",
          () => {
            const usagePercent =
              (performance.memory.usedJSHeapSize /
                performance.memory.jsHeapSizeLimit) *
              100;
            return usagePercent < 80;
          },
          "Memory usage is within acceptable limits",
          "High memory usage detected"
        )
      );
    }

    // Process checks
    checks.forEach((check) => {
      if (check.result === "pass") passed++;
      else if (check.result === "fail") failed++;
      else warnings++;
    });

    return { category: "performance", checks, passed, failed, warnings };
  }

  // Dependencies validation
  static validateDependencies() {
    const checks = [];
    let passed = 0,
      failed = 0,
      warnings = 0;

    // Required browser APIs
    const requiredAPIs = [
      { name: "fetch", check: () => typeof fetch !== "undefined" },
      { name: "Promise", check: () => typeof Promise !== "undefined" },
      { name: "localStorage", check: () => typeof Storage !== "undefined" },
      { name: "sessionStorage", check: () => typeof Storage !== "undefined" },
    ];

    requiredAPIs.forEach((api) => {
      checks.push(
        this.createCheck(
          `API Support: ${api.name}`,
          api.check,
          `${api.name} API is supported`,
          `${api.name} API is not supported`
        )
      );
    });

    // Vue.js version check (if available)
    if (typeof window !== "undefined" && window.Vue) {
      checks.push(
        this.createCheck(
          "Vue.js Version",
          () => {
            const version = window.Vue.version || "0.0.0";
            const majorVersion = parseInt(version.split(".")[0]);
            return majorVersion >= 3;
          },
          "Vue.js version is supported",
          "Vue.js version may not be supported"
        )
      );
    }

    // Process checks
    checks.forEach((check) => {
      if (check.result === "pass") passed++;
      else if (check.result === "fail") failed++;
      else warnings++;
    });

    return { category: "dependencies", checks, passed, failed, warnings };
  }

  // Browser support validation
  static validateBrowserSupport() {
    const checks = [];
    let passed = 0,
      failed = 0,
      warnings = 0;

    if (typeof navigator === "undefined") {
      return {
        category: "browser",
        checks: [],
        passed: 0,
        failed: 0,
        warnings: 0,
      };
    }

    // User agent parsing
    const userAgent = navigator.userAgent.toLowerCase();

    // Modern browser check
    checks.push(
      this.createCheck(
        "Modern Browser Support",
        () => {
          // Check for modern browser features
          return (
            "IntersectionObserver" in window &&
            "customElements" in window &&
            "fetch" in window
          );
        },
        "Browser supports modern features",
        "Browser may not support all modern features"
      )
    );

    // Mobile vs Desktop
    const isMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent
      );
    checks.push(
      this.createCheck(
        "Device Type Detection",
        () => true, // Always pass, just informational
        `Detected device type: ${isMobile ? "Mobile" : "Desktop"}`,
        "Could not determine device type"
      )
    );

    // WebGL support (for advanced features)
    checks.push(
      this.createCheck(
        "WebGL Support",
        () => {
          const canvas = document.createElement("canvas");
          return !!(
            canvas.getContext("webgl") ||
            canvas.getContext("experimental-webgl")
          );
        },
        "WebGL is supported",
        "WebGL is not supported"
      )
    );

    // Process checks
    checks.forEach((check) => {
      if (check.result === "pass") passed++;
      else if (check.result === "fail") failed++;
      else warnings++;
    });

    return { category: "browser", checks, passed, failed, warnings };
  }

  // Create validation check object
  static createCheck(name, testFunction, successMessage, failureMessage) {
    let result, message;

    try {
      const passed = testFunction();
      result = passed ? "pass" : "fail";
      message = passed ? successMessage : failureMessage;
    } catch (error) {
      result = "warn";
      message = `Error during check: ${error.message}`;
    }

    return {
      name,
      result,
      message,
      timestamp: new Date().toISOString(),
    };
  }

  // Log validation summary
  static logValidationSummary(results) {
    const { overall, categories } = results;
    const total = overall.passed + overall.failed + overall.warnings;

    Logger.info("🔍 Validation Summary:");
    Logger.info(`   Total Checks: ${total}`);
    Logger.info(`   ✅ Passed: ${overall.passed}`);
    Logger.info(`   ❌ Failed: ${overall.failed}`);
    Logger.info(`   ⚠️  Warnings: ${overall.warnings}`);

    // Log category summaries
    Object.entries(categories).forEach(([category, data]) => {
      const categoryTotal = data.passed + data.failed + data.warnings;
      Logger.info(`   ${category}: ${data.passed}/${categoryTotal} passed`);

      // Log failures and warnings
      data.checks.forEach((check) => {
        if (check.result === "fail") {
          Logger.error(`     ❌ ${check.name}: ${check.message}`);
        } else if (check.result === "warn") {
          Logger.warn(`     ⚠️  ${check.name}: ${check.message}`);
        }
      });
    });

    // Overall status
    if (overall.failed === 0) {
      Logger.info("✅ All critical validations passed");
    } else {
      Logger.error(`❌ ${overall.failed} critical validation(s) failed`);
    }
  }

  // Get validation results
  static getValidationResults(type = "full") {
    return this.validationResults.get(type);
  }

  // Quick health check
  static quickHealthCheck() {
    const checks = [
      {
        name: "Environment",
        test: () => !!EnvironmentConfig.environment,
      },
      {
        name: "Firebase",
        test: () => !!EnvironmentConfig.firebase.projectId,
      },
      {
        name: "Stripe",
        test: () =>
          !!StripeConfig.config.publishableKey ||
          FeatureFlags.payments.mockPayments,
      },
    ];

    const results = checks.map((check) => ({
      name: check.name,
      healthy: check.test(),
      timestamp: new Date().toISOString(),
    }));

    const allHealthy = results.every((result) => result.healthy);

    return {
      overall: allHealthy ? "healthy" : "unhealthy",
      checks: results,
      timestamp: new Date().toISOString(),
    };
  }

  // Continuous monitoring setup
  static setupContinuousValidation(intervalMs = 300000) {
    // 5 minutes default
    if (EnvironmentConfig.isDevelopment) {
      const interval = setInterval(() => {
        const health = this.quickHealthCheck();
        if (health.overall === "unhealthy") {
          Logger.warn("Health check failed", health);
        }
      }, intervalMs);

      // Return cleanup function
      return () => clearInterval(interval);
    }
    return () => {}; // No-op for non-development environments
  }
}

export default ValidationService;
